<script setup lang="ts">
import { useAuthStore } from '@proj-airi/stage-ui/stores/auth'
import { useAiriCardStore } from '@proj-airi/stage-ui/stores/modules/airi-card'
import StageConversation from '../components/StageConversation.vue'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { useHoloRuntimeAuthorityStore } from '../holo/holo-runtime-authority-store'

const authStore = useAuthStore()
const cardStore = useAiriCardStore()
const authorityStore = useHoloRuntimeAuthorityStore()

const { isAuthenticated, user } = storeToRefs(authStore)
const { activeCardId, cards } = storeToRefs(cardStore)
const { phase, lastError, runtimeLease, isActive, heldByAnotherRuntime } = storeToRefs(authorityStore)
const acting = ref(false)

const characterOptions = computed(() => [...cards.value].map(([id, card]) => ({
  id,
  name: card.name || id,
})))

const currentPhase = computed(() => {
  if (isActive.value)
    return 'ACTIVE · 当前终端运行中'
  switch (phase.value) {
    case 'blocked': return '不可运行 · 角色尚无服务端 Card 权威'
    case 'preparing': return 'PREPARING · 正在准备角色'
    case 'activating': return 'ACTIVATING · 正在申请运行权限'
    case 'error': return '无法确认角色权限'
    default: return 'PASSIVE · 当前终端未运行角色'
  }
})

async function chooseCard(event: Event) {
  const id = (event.target as HTMLSelectElement).value
  if (id)
    await cardStore.activateCard(id)
}

async function runHere() {
  if (acting.value || !isAuthenticated.value)
    return
  acting.value = true
  try {
    if (heldByAnotherRuntime.value)
      await authorityStore.takeover()
    else
      await authorityStore.activate()
  }
  finally {
    acting.value = false
  }
}
</script>

<template>
  <main class="shell">
    <header class="topbar">
      <div>
        <p class="eyebrow">
          Fullmoonlit
        </p>
        <h1>Holo</h1>
      </div>
      <button v-if="isAuthenticated" class="secondary" type="button" @click="authStore.signOut()">
        退出登录
      </button>
    </header>

    <section v-if="!isAuthenticated" class="hero">
      <p class="eyebrow">
        AIRI 原生身份
      </p>
      <h2>你的角色，运行在你的终端。</h2>
      <p>使用 AIRI 账号登录。角色运行与实体 Holo 设备连接是两个独立步骤。</p>
      <button type="button" @click="authStore.requestLogin()">
        登录
      </button>
    </section>

    <template v-else>
      <section class="account">
        <div>
          <p class="eyebrow">
            当前 AIRI 账号
          </p>
          <strong>{{ user?.name || user?.email || '已登录' }}</strong>
        </div>
        <span class="status-pill">{{ currentPhase }}</span>
      </section>

      <section class="devices">
        <div class="section-heading">
          <div>
            <p class="eyebrow">
              角色运行
            </p>
            <h2>当前角色</h2>
          </div>
        </div>

        <label for="stage-character">选择 AIRI Character Card（角色卡）</label>
        <select
          id="stage-character"
          :value="activeCardId"
          :disabled="acting"
          @change="chooseCard"
        >
          <option v-for="character in characterOptions" :key="character.id" :value="character.id">
            {{ character.name }}
          </option>
        </select>

        <p class="notice">
          {{ currentPhase }}
        </p>
        <p v-if="lastError" class="notice error-text">
          {{ lastError }}
        </p>
        <p v-if="phase === 'blocked'" class="notice">
          当前角色卡尚未通过 AIRI 服务端所有权验证。仅保存在浏览器中的卡不能启动角色。
        </p>
        <p v-else-if="heldByAnotherRuntime" class="notice">
          此 AIRI 账号已有另一终端正在运行角色（可能是不同角色）。你可以在本终端接管账号运行权，旧终端将停止运行。
        </p>
        <p v-else-if="isActive" class="notice">
          当前终端已拥有角色运行权限。默认交互与显示目标均为本地终端。
        </p>

        <button
          v-if="!isActive && phase !== 'blocked' && phase !== 'preparing' && phase !== 'activating'"
          type="button"
          :disabled="acting || !activeCardId"
          @click="runHere"
        >
          {{ acting ? '正在申请…' : '在此设备运行' }}
        </button>

        <p v-if="runtimeLease && !isActive" class="notice">
          当前运行由服务端 Runtime Lease（运行租约）协调，不会同时启动两份独立角色。
        </p>
        <p class="notice">
          实体设备绑定、交互/显示设备选择将在 R3/D1 接入，本阶段不恢复旧 Wake/Sleep（唤醒/休眠）或双显示模式。
        </p>
      </section>

      <StageConversation />
    </template>
  </main>
</template>
